// Backend base URL — set REACT_APP_API_BASE_URL at build time when the
// frontend is served from a different host/origin than the API server.
// Leave unset for same-origin deployments (frontend served by the Express app).
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const getToken = () => localStorage.getItem('portal_token') || '';

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function apiFetch(path, opts = {}) {
  const { skipAuthRedirect, ...fetchOpts } = opts;
  const res = await fetch(`${API_BASE}${path}`, {
    ...fetchOpts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(fetchOpts.headers || {}) },
  });
  if (res.status === 401 && !skipAuthRedirect) {
    localStorage.removeItem('portal_token');
    window.location.reload();
    return;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export const login = (email, password) =>
  apiFetch('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }), skipAuthRedirect: true });

export const verifyMfa = (mfaToken, code) =>
  apiFetch('/api/auth/mfa-verify', { method: 'POST', body: JSON.stringify({ mfaToken, code }), skipAuthRedirect: true });

export const setupMfa   = ()     => apiFetch('/api/auth/mfa-setup');
export const enableMfa  = (code) => apiFetch('/api/auth/mfa-enable',  { method: 'POST',   body: JSON.stringify({ code }) });
export const disableMfa = (code) => apiFetch('/api/auth/mfa-disable', { method: 'DELETE', body: JSON.stringify({ code }) });

export const getMe = () => apiFetch('/api/auth/me');

export const listCustomers = () => apiFetch('/api/customers');

export const createCustomer = (data) =>
  apiFetch('/api/customers', { method: 'POST', body: JSON.stringify(data) });

export const deleteCustomer = (id) =>
  apiFetch(`/api/customers/${id}`, { method: 'DELETE' });

export const getCustomerModules = (id) =>
  apiFetch(`/api/customers/${id}/modules`);
export const setCustomerModules = (id, modules) =>
  apiFetch(`/api/customers/${id}/modules`, { method: 'PUT', body: JSON.stringify({ modules }) });

// Per-customer Voxbone routing-API override. getCustomerVoxbone returns
// { base, authType, hasKey } (never the raw key). setCustomerVoxbone sends
// { base, authType, key? }: omit key to leave it unchanged, send key:'__clear__'
// to remove it, or a new key string to replace it.
export const getCustomerVoxbone = (id) =>
  apiFetch(`/api/customers/${id}/voxbone`);
export const setCustomerVoxbone = (id, data) =>
  apiFetch(`/api/customers/${id}/voxbone`, { method: 'PUT', body: JSON.stringify(data) });
// Read-only connectivity test of the customer's resolved Voxbone account.
// Returns { ok, status?, override, base, count?, error? }.
export const testCustomerVoxbone = (id) =>
  apiFetch(`/api/customers/${id}/voxbone/test`);

export const listDids = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/api/dids${qs ? '?' + qs : ''}`);
};

export const syncDids = () =>
  apiFetch('/api/admin/sync-dids', { method: 'POST' });

export const assignDids = (didIds, customerId) =>
  apiFetch('/api/admin/assign-dids', {
    method: 'PUT',
    body: JSON.stringify({ didIds, customerId }),
  });

export const activatePstnRoute = (didIds, e164) =>
  apiFetch('/api/activate-pstn', {
    method: 'POST',
    body: JSON.stringify({ didIds, e164 }),
  });

export const updateCustomer = (id, data) =>
  apiFetch(`/api/customers/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const listCustomerEmails = (customerId) =>
  apiFetch(`/api/customers/${customerId}/emails`);

export const addCustomerEmail = (customerId, email, password) =>
  apiFetch(`/api/customers/${customerId}/emails`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

export const removeCustomerEmail = (customerId, emailId) =>
  apiFetch(`/api/customers/${customerId}/emails/${emailId}`, { method: 'DELETE' });

export const listVoiceUris = () => apiFetch('/api/voice-uris');

export const syncVoiceUris = () =>
  apiFetch('/api/admin/sync-voice-uris', { method: 'POST' });

export const createVoiceUri = (data) =>
  apiFetch('/api/voice-uris', { method: 'POST', body: JSON.stringify(data) });

export const updateVoiceUri = (id, data) =>
  apiFetch(`/api/voice-uris/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteVoiceUri = (id) =>
  apiFetch(`/api/voice-uris/${id}`, { method: 'DELETE' });

export const listSbcs = () => apiFetch('/api/sbcs');

export const createSbc = (data) =>
  apiFetch('/api/sbcs', { method: 'POST', body: JSON.stringify(data) });

export const updateSbc = (id, data) =>
  apiFetch(`/api/sbcs/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteSbc = (id) =>
  apiFetch(`/api/sbcs/${id}`, { method: 'DELETE' });

export const pollSbc = (id) =>
  apiFetch(`/api/sbcs/${id}/poll`, { method: 'POST' });

export const fetchCdrs = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/api/cdrs${qs ? '?' + qs : ''}`);
};

export const syncCdrs = () =>
  apiFetch('/api/admin/sync-cdrs', { method: 'POST' });

export const getAlerts = () => apiFetch('/api/alerts');

export const getBandwidthRouting = () => apiFetch('/api/bandwidth/routing');
export const saveBandwidthRouting = (number, data) =>
  apiFetch(`/api/bandwidth/routing/${encodeURIComponent(number)}`, { method: 'PUT', body: JSON.stringify(data) });
export const syncBandwidthRouting = () =>
  apiFetch('/api/bandwidth/routing/sync', { method: 'POST' });
export const fetchBandwidthNumbers = () =>
  apiFetch('/api/bandwidth/routing/fetch-numbers', { method: 'POST' });

export const syncBandwidthCdrs = () =>
  apiFetch('/api/admin/sync-bandwidth-cdrs', { method: 'POST' });

export const discoverBandwidthAccounts = () =>
  apiFetch('/api/admin/discover-bandwidth-accounts', { method: 'POST' });

export const listPlatforms = (customerId) => {
  const qs = customerId ? `?customerId=${customerId}` : '';
  return apiFetch(`/api/platforms${qs}`);
};

export const updateCustomerPlatform = (platformId, data, customerId) =>
  apiFetch(`/api/platforms/${platformId}/customer-config`, {
    method: 'PUT',
    body: JSON.stringify({ ...data, ...(customerId !== undefined ? { customerId } : {}) }),
  });

// Per-customer platform enablement (the + / remove on the Platforms page).
export const enableCustomerPlatform = (customerId, platformId) =>
  apiFetch(`/api/customers/${customerId}/platforms`, {
    method: 'POST',
    body: JSON.stringify({ platformId }),
  });
export const disableCustomerPlatform = (customerId, platformId) =>
  apiFetch(`/api/customers/${customerId}/platforms/${platformId}`, { method: 'DELETE' });

// Customer-owned custom platforms (e.g. per-customer On-Premise SBC systems).
export const createCustomPlatform = (customerId, data) =>
  apiFetch(`/api/customers/${customerId}/custom-platforms`, { method: 'POST', body: JSON.stringify(data) });
export const deleteCustomPlatform = (customerId, platformId) =>
  apiFetch(`/api/customers/${customerId}/custom-platforms/${platformId}`, { method: 'DELETE' });

// Import Number Routing from a live SBC dial plan (preview, then apply).
export const importRoutingFromSbc = (sbcId, apply = false) =>
  apiFetch('/api/routing/import-from-sbc', { method: 'POST', body: JSON.stringify({ sbcId, apply }) });

export const updatePlatformVoiceUris = (platformId, data) =>
  apiFetch(`/api/platforms/${platformId}/voice-uris`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const updatePlatform = (platformId, data) =>
  apiFetch(`/api/platforms/${platformId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const getRoutingProfiles = () => apiFetch('/api/routing');

export const saveRoutingProfile = (didId, data) =>
  apiFetch(`/api/routing/${encodeURIComponent(didId)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const activateRouting = (didIds, routeType) =>
  apiFetch('/api/routing/activate', {
    method: 'POST',
    body: JSON.stringify({ didIds, routeType }),
  });

// Reconcile did_routing.active_route from local dids.voice_uri_id (no Voxbone API call)
export const reconcileRouting = () =>
  apiFetch('/api/routing/reconcile', { method: 'POST' });

export const listSites = () => apiFetch('/api/sites');
export const createSite = (name, customerId) => apiFetch('/api/sites', { method: 'POST', body: JSON.stringify({ name, customerId: customerId ?? null }) });
export const updateSite = (id, name, customerId, vrpName, alertOnBusy) => {
  const body = { name };
  if (customerId !== undefined) body.customerId = customerId;
  if (vrpName !== undefined) body.vrpName = vrpName ?? null;
  if (alertOnBusy !== undefined) body.alertOnBusy = Boolean(alertOnBusy);
  return apiFetch(`/api/sites/${id}`, { method: 'PUT', body: JSON.stringify(body) });
};
export const deleteSite = (id) => apiFetch(`/api/sites/${id}`, { method: 'DELETE' });
export const assignSite = (didIds, siteId) => apiFetch('/api/dids/assign-site', { method: 'PUT', body: JSON.stringify({ didIds, siteId }) });

// Voice Routing Policies
export const listVoiceRoutingPolicies = (customerId) => {
  const qs = customerId ? `?customerId=${customerId}` : '';
  return apiFetch(`/api/teams/voice-routing-policies${qs}`);
};
export const addVoiceRoutingPolicy = (customerId, name, description = '') =>
  apiFetch('/api/teams/voice-routing-policies', { method: 'POST', body: JSON.stringify({ customerId, name, description }) });
export const importVoiceRoutingPolicies = (customerId, policies) =>
  apiFetch('/api/teams/voice-routing-policies/import', { method: 'POST', body: JSON.stringify({ customerId, policies }) });
export const syncVoiceRoutingPolicies = (customerId) =>
  apiFetch('/api/teams/voice-routing-policies/sync', { method: 'POST', body: JSON.stringify({ customerId }) });
export const deleteVoiceRoutingPolicy = (id) =>
  apiFetch(`/api/teams/voice-routing-policies/${id}`, { method: 'DELETE' });

// Teams / Microsoft 365
export const getTeamsConnection   = (customerId) => apiFetch(`/api/teams/connection${customerId ? '?customerId=' + customerId : ''}`);
export const saveTeamsConnection  = (data) => apiFetch('/api/teams/connection', { method: 'POST', body: JSON.stringify(data) });
export const deleteTeamsConnection = (customerId) => apiFetch(`/api/teams/connection${customerId ? '?customerId=' + customerId : ''}`, { method: 'DELETE' });
export const testTeamsConnection  = (customerId) => apiFetch(`/api/teams/connection/test`, { method: 'POST', body: JSON.stringify({ customerId }) });
export const getTeamsUsers        = (customerId) => apiFetch(`/api/teams/users${customerId ? '?customerId=' + customerId : ''}`);
export const syncTeamsUsers       = (customerId) => apiFetch('/api/teams/users/sync', { method: 'POST', body: JSON.stringify({ customerId }) });
export const assignTeamsNumber    = (msUserId, phoneNumber, upn, currentPhone, customerId) =>
  apiFetch(`/api/teams/users/${encodeURIComponent(msUserId)}/assign-number`, {
    method: 'POST', body: JSON.stringify({ phoneNumber, upn, currentPhone, customerId }),
  });

export const getTeamsOAuthUrl = (customerId) =>
  apiFetch(`/api/teams/oauth/start${customerId ? '?customerId=' + customerId : ''}`);

export const getCallQuality = (customerId, days = 30) => {
  const qs = new URLSearchParams({ ...(customerId ? { customerId } : {}), days }).toString();
  return apiFetch(`/api/teams/call-quality?${qs}`);
};

export const syncCallQuality = (customerId) =>
  apiFetch('/api/teams/call-quality/sync', { method: 'POST', body: JSON.stringify({ customerId }) });

export const getDashboardStats = (customerId) => {
  const qs = customerId ? `?customerId=${customerId}` : '';
  return apiFetch(`/api/dashboard${qs}`);
};

// Zoom Phone
export const getZoomConnection = (customerId) =>
  apiFetch(`/api/zoom/connection${customerId ? '?customerId=' + customerId : ''}`);

export const saveZoomConnection = (data) =>
  apiFetch('/api/zoom/connection', { method: 'POST', body: JSON.stringify(data) });

export const deleteZoomConnection = (customerId) =>
  apiFetch(`/api/zoom/connection${customerId ? '?customerId=' + customerId : ''}`, { method: 'DELETE' });

export const testZoomConnection = (customerId) =>
  apiFetch('/api/zoom/connection/test', { method: 'POST', body: JSON.stringify({ customerId }) });

export const getZoomUsers = (customerId) =>
  apiFetch(`/api/zoom/users${customerId ? '?customerId=' + customerId : ''}`);

export const syncZoomUsers = (customerId) =>
  apiFetch('/api/zoom/users/sync', { method: 'POST', body: JSON.stringify({ customerId }) });

export const assignZoomNumber = (userId, phoneNumber, phoneNumberId, customerId) =>
  apiFetch(`/api/zoom/users/${encodeURIComponent(userId)}/assign-number`, {
    method: 'POST',
    body: JSON.stringify({ phoneNumber, phoneNumberId, customerId }),
  });

export const setTeamsUserVrp = (msUserId, vrpName, customerId) =>
  apiFetch(`/api/teams/users/${encodeURIComponent(msUserId)}/set-vrp`, {
    method: 'POST',
    body: JSON.stringify({ vrpName, customerId }),
  });

export const debugTeamsUser = (customerId, upn) =>
  apiFetch(`/api/teams/users/debug?customerId=${customerId}&upn=${encodeURIComponent(upn)}`);

export const getSbcDialPlan = (id) =>
  apiFetch(`/api/sbcs/${id}/dialplan`);

export const getSbcDialPlanRaw = (id) =>
  apiFetch(`/api/sbcs/${id}/dialplan?raw=1`);

// Full running-config dump (carrier topology), passwords redacted server-side.
export const getSbcConfigFull = (id) =>
  apiFetch(`/api/sbcs/${id}/dialplan?full=1`);

// ── Carriers (PSTN/ITSP trunks) ──
export const listCarriers = () => apiFetch('/api/carriers');
export const createCarrier = (data) =>
  apiFetch('/api/carriers', { method: 'POST', body: JSON.stringify(data) });
export const updateCarrier = (id, data) =>
  apiFetch(`/api/carriers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteCarrier = (id) =>
  apiFetch(`/api/carriers/${id}`, { method: 'DELETE' });
export const setCarrierSbcs = (id, sbcs) =>
  apiFetch(`/api/carriers/${id}/sbcs`, { method: 'PUT', body: JSON.stringify({ sbcs }) });
export const setDidCarrier = (didId, carrierId) =>
  apiFetch(`/api/dids/${encodeURIComponent(didId)}/carrier`, { method: 'PUT', body: JSON.stringify({ carrierId }) });
export const setDidsCarrierBulk = (didIds, carrierId) =>
  apiFetch('/api/dids/carrier/bulk', { method: 'PUT', body: JSON.stringify({ didIds, carrierId }) });
export const inferCarriers = (overwrite = false) =>
  apiFetch('/api/carriers/infer', { method: 'POST', body: JSON.stringify({ overwrite }) });

export const addSbcDialPlanEntry = (id, data) =>
  apiFetch(`/api/sbcs/${id}/dialplan`, { method: 'POST', body: JSON.stringify(data) });

export const deleteSbcDialPlanEntry = (id, index) =>
  apiFetch(`/api/sbcs/${id}/dialplan/${index}`, { method: 'DELETE' });

export const getSbcPollConfig = () =>
  apiFetch('/api/admin/sbc-poll-config');

export const setSbcPollConfig = (intervalSecs) =>
  apiFetch('/api/admin/sbc-poll-config', { method: 'PUT', body: JSON.stringify({ intervalSecs }) });

// CDR retention
export const getCdrRetention = () => apiFetch('/api/admin/cdr-retention');
export const setCdrRetention = (days) =>
  apiFetch('/api/admin/cdr-retention', { method: 'PUT', body: JSON.stringify({ days }) });
export const pruneCdrsNow = () => apiFetch('/api/admin/cdr-retention/prune', { method: 'POST' });

// Admin change log (audit + selective restore)
export const getChangeLog = (limit = 200) =>
  apiFetch(`/api/admin/change-log?limit=${limit}`);
export const restoreChange = (id) =>
  apiFetch(`/api/admin/change-log/${id}/restore`, { method: 'POST' });

export const getPowershellStatus = () =>
  apiFetch('/api/admin/powershell-status');

export const installPowershellModule = () =>
  apiFetch('/api/admin/powershell-install-module', { method: 'POST' });

// Call Queues
export const listCallQueues = (customerId) => apiFetch(`/api/call-queues${customerId ? `?customerId=${customerId}` : ''}`);
export const syncCallQueues = (customerId) => apiFetch('/api/call-queues/sync', { method: 'POST', body: JSON.stringify({ customerId }) });
export const createCallQueue = (data) => apiFetch('/api/call-queues', { method: 'POST', body: JSON.stringify(data) });
export const updateCallQueue = (id, data) => apiFetch(`/api/call-queues/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteCallQueue = (id) => apiFetch(`/api/call-queues/${id}`, { method: 'DELETE' });
export const getQueueMembers = (id) => apiFetch(`/api/call-queues/${id}/members`);
export const updateQueueMembers = (id, data) => apiFetch(`/api/call-queues/${id}/members`, { method: 'PUT', body: JSON.stringify(data) });
export const searchEntra = (q, type, customerId) => {
  const p = new URLSearchParams({ q, type });
  if (customerId) p.set('customerId', String(customerId));
  return apiFetch(`/api/entra/search?${p}`);
};

// Resource Accounts
export const listResourceAccounts = (customerId) => apiFetch(`/api/resource-accounts${customerId ? `?customerId=${customerId}` : ''}`);
export const createResourceAccount = (data) => apiFetch('/api/resource-accounts', { method: 'POST', body: JSON.stringify(data) });
export const updateResourceAccount = (id, data) => apiFetch(`/api/resource-accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteResourceAccount = (id) => apiFetch(`/api/resource-accounts/${id}`, { method: 'DELETE' });
export const assignLicense = (id, action = 'assign') => apiFetch(`/api/resource-accounts/${id}/license`, { method: 'POST', body: JSON.stringify({ action }) });

// Auto Attendants
export const listAutoAttendants  = (customerId) => apiFetch(`/api/auto-attendants${customerId ? `?customerId=${customerId}` : ''}`);
export const syncAutoAttendants  = (customerId) => apiFetch('/api/auto-attendants/sync', { method: 'POST', body: JSON.stringify({ customerId }) });
export const createAutoAttendant = (data)       => apiFetch('/api/auto-attendants', { method: 'POST', body: JSON.stringify(data) });
export const updateAutoAttendant = (id, data)   => apiFetch(`/api/auto-attendants/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteAutoAttendant = (id)         => apiFetch(`/api/auto-attendants/${id}`, { method: 'DELETE' });

export async function pollJob(jobId, onResult, { intervalMs = 3000, maxMs = 180000 } = {}) {
  if (!jobId) return;
  const start = Date.now();
  const timer = setInterval(async () => {
    if (Date.now() - start > maxMs) { clearInterval(timer); return; }
    try {
      const job = await apiFetch(`/api/jobs/${jobId}`);
      if (job.status === 'done' || job.status === 'error') {
        clearInterval(timer);
        onResult(job);
      }
    } catch {}
  }, intervalMs);
}
